import {
  AgBarSeriesOptions,
  AgCartesianAxisOptions,
  AgCartesianSeriesOptions,
  AgChartOptions,
  AgSeriesVisibilityChange,
} from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import { TFunction, useTranslation } from 'translations/hooks';
import { getAgChartLocale } from 'translations/locale';

import {
  RingPerDayLegendFilter,
  useRingPerDayLegendFilter,
} from 'business/production-and-performance/pages/ring-per-day/providers/legend-filter';
import { RingPerDayGraphPoint } from 'business/production-and-performance/pages/ring-per-day/types';
import { useProdAndPerfFilters } from 'business/production-and-performance/providers/prod-and-perf-filters-provider';
import { useSingleGraphReferenceContext } from 'business/providers/graph-reference';
import { useChartResize } from 'technical/chart-resize';
import { hasInitialZoom } from 'technical/graph/has-initial-zoom';
import { useMediaType } from 'technical/media/hooks';
import { formatDateToDayAndMonth } from 'technical/time-utils/format-date-to-day-and-month';

type RingPerDayGraphProps = {
  ringPerDayGraphData: RingPerDayGraphPoint[];
  shift1Defined: boolean;
  shift2Defined: boolean;
  shift3Defined: boolean;
};

const tooltipRenderer = (params: any, t: TFunction) => {
  return {
    data: [
      {
        label: '',
        value: t('productionAndPerformance.ringPerDay.graph.tooltip.ring', {
          count: parseInt(params.datum[params.yKey]),
        }),
      },
    ],
  };
};

const ringPerDayShiftGraphSeriesOptions = (
  t: TFunction,
  visibility: RingPerDayLegendFilter,
  shiftsDefined: {
    shift1Defined: boolean;
    shift2Defined: boolean;
    shift3Defined: boolean;
  },
) => {
  const shift1Series: AgBarSeriesOptions = {
    type: 'bar',
    xKey: 'date',
    yKey: 'shift1Count',
    yName: t('productionAndPerformance.ringPerDay.graph.seriesNames.shift', {
      shiftKey: 1,
    }),
    stacked: true,
    tooltip: { renderer: (params) => tooltipRenderer(params, t) },
    visible: visibility.shift1Count,
  };

  const shift2Series: AgBarSeriesOptions = {
    type: 'bar',
    xKey: 'date',
    yKey: 'shift2Count',
    yName: t('productionAndPerformance.ringPerDay.graph.seriesNames.shift', {
      shiftKey: 2,
    }),
    stacked: true,
    tooltip: { renderer: (params) => tooltipRenderer(params, t) },
    visible: visibility.shift2Count,
  };

  const shift3Series: AgBarSeriesOptions = {
    type: 'bar',
    xKey: 'date',
    yKey: 'shift3Count',
    yName: t('productionAndPerformance.ringPerDay.graph.seriesNames.shift', {
      shiftKey: 3,
    }),
    stacked: true,
    tooltip: { renderer: (params) => tooltipRenderer(params, t) },
    visible: visibility.shift3Count,
  };

  return [
    shiftsDefined.shift1Defined ? shift1Series : null,
    shiftsDefined.shift2Defined ? shift2Series : null,
    shiftsDefined.shift3Defined ? shift3Series : null,
  ].filter((series): series is AgBarSeriesOptions => series !== null);
};

const ringPerDayCalendarGraphSeriesOptions = (t: TFunction) =>
  [
    {
      type: 'bar',
      xKey: 'date',
      yKey: 'count',
      tooltip: {
        renderer: (params) => tooltipRenderer(params, t),
      },
    },
  ] satisfies AgCartesianSeriesOptions[];

const ringPerDayGraphAxesOptions = () =>
  [
    {
      type: 'category',
      position: 'bottom',
      label: {
        formatter: ({ value }) => formatDateToDayAndMonth(value),
      },
    },
    {
      type: 'number',
      position: 'left',
      nice: false,
      interval: {
        step: 1,
      },
    },
  ] satisfies AgCartesianAxisOptions[];

export function RingPerDayGraph({
  ringPerDayGraphData,
  shift1Defined,
  shift2Defined,
  shift3Defined,
}: RingPerDayGraphProps) {
  const { ref: chartRef } = useSingleGraphReferenceContext();
  const { isMobile } = useMediaType();
  const { t, language } = useTranslation();
  const filters = useProdAndPerfFilters();
  const { legendFilter, setLegendFilter } = useRingPerDayLegendFilter();

  const updateVisibility = ({ itemId, visible }: AgSeriesVisibilityChange) => {
    if (itemId && typeof itemId === 'string') {
      setLegendFilter((value) => {
        const updatedVisibility = {
          ...value,
          [itemId]: visible,
        };
        return updatedVisibility;
      });
    }
  };

  const initialZoom = hasInitialZoom(isMobile, filters);
  const chartOptions = {
    data: ringPerDayGraphData,
    series:
      filters.dayMode === 'shift'
        ? ringPerDayShiftGraphSeriesOptions(t, legendFilter, {
            shift1Defined,
            shift2Defined,
            shift3Defined,
          })
        : ringPerDayCalendarGraphSeriesOptions(t),
    axes: ringPerDayGraphAxesOptions(),
    listeners: {
      seriesVisibilityChange: updateVisibility,
    },
    locale: {
      localeText: getAgChartLocale(language),
    },
    ...(isMobile
      ? {
          touch: { dragAction: 'drag' },
          zoom: {
            enabled: true,
            enableAxisDragging: false,
            enableTwoFingerZoom: true,
            buttons: {
              enabled: false,
            },
          },
          initialState: {
            zoom: initialZoom
              ? {
                  ratioX: {
                    start: 0,
                    end: 0.4,
                  },
                }
              : {},
          },
          contextMenu: {
            enabled: false,
          },
        }
      : {
          zoom: {
            anchorPointX: 'pointer',
            anchorPointY: 'pointer',
          },
        }),
  } satisfies AgChartOptions;

  useChartResize(chartRef);

  return <AgCharts options={chartOptions} ref={chartRef} />;
}
